import { useMemo, useState, type JSX } from 'react';

import { Select } from '@/ui';
import type { OptionType } from '@/ui/components/Form/Select/types';

import type { FieldOption } from '../../types';

interface SelectFieldProps {
  label: string | JSX.Element;
  options: FieldOption[];
  allowOther?: boolean;
  multiple?: boolean;
  value: string | string[] | null;
  onChange: (value: string | string[] | null) => void;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  description?: string;
}

const toOption = (value: string, options: FieldOption[]): OptionType<string> => ({
  value,
  label: options.find((option) => option.value === value)?.label ?? value
});

const SelectField = ({
  label,
  options,
  allowOther,
  multiple,
  value,
  onChange,
  error,
  disabled,
  placeholder,
  description
}: SelectFieldProps) => {
  const [query, setQuery] = useState('');

  const selectOptions = useMemo(() => {
    const base: OptionType<string>[] = options.map((option) => ({
      value: option.value,
      label: option.label
    }));
    // "Other" answers are free text: surface the current query as a pickable
    // option unless it already collides with an existing option's value or
    // label, since option values must stay unique.
    if (
      allowOther &&
      query &&
      !base.some((option) => option.value === query || option.label === query)
    ) {
      base.push({ value: query, label: query });
    }
    return base;
  }, [options, allowOther, query]);

  if (multiple) {
    const selected = Array.isArray(value) ? value : [];
    return (
      <Select
        multiple
        label={label}
        disabled={disabled}
        error={error}
        options={selectOptions}
        placeholder={placeholder}
        helpText={description}
        onSearch={allowOther ? setQuery : undefined}
        selectedOptions={selected.map((v) => toOption(v, options))}
        onChange={(next) => onChange(next.map((option) => option.value))}
      />
    );
  }

  return (
    <Select
      label={label}
      disabled={disabled}
      error={error}
      options={selectOptions}
      placeholder={placeholder}
      helpText={description}
      onSearch={allowOther ? setQuery : undefined}
      selectedOption={typeof value === 'string' && value ? toOption(value, options) : null}
      onChange={(option) => onChange(option?.value ?? null)}
    />
  );
};

export default SelectField;
